import { formatKM, formatAmount, toFixedTwo } from "helpers/helpers";

/**
 * tableData: 原表格数据（二维数组）
 * activIndex: 当前激活行
 * noAnalysisLocationRef: 未分析点
 * analysedLegends: 图例
 * analysedLocationSet: 当前分析配置
 * t: i18n
 */
export const handleGetBarChartGroupData = ({
    tableData,
    activIndex,
    noAnalysisLocationRef = { current: [] },
    analysedLegends,
    distanceAxisConfig,
    analysedLocationSet,
    currentMultipleLocationsAnalysed,
    t,
}: {
    tableData: any[][];
    activIndex: number;
    noAnalysisLocationRef?: React.RefObject<any[]>;
    analysedLegends?: any[];
    distanceAxisConfig?: {
        enabled?: boolean;
        unit?: "m" | "km";
        max?: number;
    };
    analysedLocationSet?: any;
    currentMultipleLocationsAnalysed?: any;
    t: any;
}) => {
    const resolvedAnalysedLocationSet =
        analysedLocationSet ?? currentMultipleLocationsAnalysed;

    const unit =
        resolvedAnalysedLocationSet?.aggregationUnit ??
        "numberOfLocations";
    const isNumberOfLocations = unit === "numberOfLocations";

    /** 未分析点 */
    const noAnalysisLocationData = [
        ...(noAnalysisLocationRef.current ?? []),
    ];

    /** 1️⃣ 过滤 checked 行 */
    let rows: any[][] = tableData.map((row) =>
        row.filter((d) => d?.checked)
    );

    let isFallbackMode = false;

    /** 2️⃣ fallback：只有未分析数据 */
    if (
        rows.length === 0 &&
        noAnalysisLocationData.length > 0 &&
        analysedLegends &&
        analysedLegends.length > 0
    ) {
        isFallbackMode = true;
        rows = analysedLegends.map((legend: any) =>
            noAnalysisLocationData.map((v: any) => ({
                layerId: legend.id ?? legend.layerId,
                ...legend,
                pointId: v.id,
                valueLabel: "Not Analyzed",
                sortIndex: -1,
                checked: true,
                value: 1,
                locationsNum: 1,
                itemStyle: { color: "#f2f2f2", opacity: 1 },
                [unit]: v[unit],
            }))
        );
    }

    /** 无数据直接返回 */
    if (rows.length === 0) {
        return {
            Ynum: 0,
            option: {
                series: [],
                xAxis: { max: 1 },
                yAxis: { data: [] },
            },
        };
    }

    /** ================== 核心处理 ================== */
    const processedRows: any[][] = [];
    const rowTotalsCount: number[] = [];
    const rowTotalsUnit: number[] = [];

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
        const row = rows[rowIndex];
        const base = row[0] ?? {};

        /** label 聚合 */
        const aggMap = new Map<
            string,
            { sum: number; count: number; firstIndex: number }
        >();

        const outRow: any[] = [];
        let totalCount = 0;
        let totalUnit = 0;

        /** 统一处理一条数据 */
        const handleItem = (it: any, idx: number) => {
            const label = it.valueLabel;
            const add = isNumberOfLocations
                ? 1
                : Math.round(Number(it?.[unit]) || 0);

            const existed = aggMap.get(label);
            if (!existed) {
                aggMap.set(label, {
                    sum: add,
                    count: 1,
                    firstIndex: idx,
                });
            } else {
                existed.sum += add;
                existed.count += 1;
            }

            totalCount += 1;
            if (!isNumberOfLocations) totalUnit += add;

            outRow.push({ ...it });
        };

        /** 原始分析数据 */
        row.forEach((it, idx) => handleItem(it, idx));

        /** 补 Not Analyzed（非 fallback 才补） */
        if (!isFallbackMode && noAnalysisLocationData.length > 0) {
            noAnalysisLocationData.forEach((v) => {
                handleItem(
                    {
                        ...base,
                        pointId: v.id,
                        valueLabel: "Not Analyzed",
                        layerId: tableData?.[rowIndex]?.[0]?.layerId,
                        sortIndex: -1,
                        checked: true,
                        value: 1,
                        locationsNum: 1,
                        itemStyle: { color: "#f2f2f2", opacity: 1 },
                        ...(isNumberOfLocations ? {} : { [unit]: v[unit] }),
                    },
                    outRow.length
                );
            });
        }

        /** 回填 value / locationsNum / all */
        const seen = new Set<string>();
        for (let i = 0; i < outRow.length; i++) {
            const it = outRow[i];
            const agg = aggMap.get(it.valueLabel)!;
            const isFirst =
                !seen.has(it.valueLabel) &&
                agg.firstIndex === i;

            if (isFirst) seen.add(it.valueLabel);

            it.locationsNum = agg.count;
            it.value = isFirst
                ? isNumberOfLocations
                    ? agg.count
                    : agg.sum
                : 0;

            if (!isNumberOfLocations && isFirst) {
                it.all = agg.sum;
            }
        }

        processedRows.push(outRow);
        rowTotalsCount.push(totalCount);
        rowTotalsUnit.push(totalUnit);
    }

    /** ================== series / axis ================== */

    const activeRow =
        processedRows[activIndex] ?? processedRows[0];
    const seriesCount = activeRow.length;

    const denomCount =
        rowTotalsCount[activIndex] ??
        rowTotalsCount[0] ??
        1;
    const denomUnit =
        rowTotalsUnit[activIndex] ??
        rowTotalsUnit[0] ??
        1;

    const Ynum = processedRows.length;

    const series: any[] = [];
    for (let i = 0; i < seriesCount; i++) {
        const dataArr: any[] = [];
        let hasValue = false;

        for (let j = processedRows.length - 1; j >= 0; j--) {
            if (processedRows[j][i]?.value) hasValue = true;
            dataArr.push(processedRows[j][i]);
        }

        if (hasValue) {
            series.push({
                name: String(i),
                type: "bar",
                stack: "total",
                barWidth: 24,
                data: dataArr,
                // barMinHeight: 4, //////******解决数值比例相差过大,导致小比例看不见问题 需echarts降到5.2.2以下
            });
        }
    }

    const xMax = isNumberOfLocations
        ? denomCount
        : denomUnit || 1;
    const useDistanceAxis = Boolean(distanceAxisConfig?.enabled);
    const distanceAxisMax = Number(distanceAxisConfig?.max);
    const finalDistanceMax = Number.isFinite(distanceAxisMax) && distanceAxisMax > 0
        ? distanceAxisMax
        : xMax;

    /** ================== option ================== */
    const option: any = {
        tooltip: {
            confine: true,
            trigger: "item",
            axisPointer: { type: "shadow" },
            backgroundColor: "rgba(0,0,0,0.6)",
            borderColor: "rgba(0,0,0,0.6)",
            formatter: (params: any) => {
                const d = params?.data ?? {};
                const denom = isNumberOfLocations ? denomCount : denomUnit || 1;
                const share =
                    denom > 0
                        ? isNumberOfLocations
                            ? (d.locationsNum ?? 0) / denom
                            : (d.all ?? 0) / denom
                        : 0;

                const shareText = toFixedTwo(share * 100);

                let unitTitle = ""
                let unitValue = 0;
                if (distanceAxisConfig?.enabled) {
                    unitTitle = distanceAxisConfig?.unit === "m" ? t("drawShape.m") : t("drawShape.km");
                    let distanceMax = (distanceAxisConfig?.max || 0) * share;
                    unitValue = parseFloat(distanceMax?.toFixed(3) || "0")
                } else {
                    unitTitle = t("locations.table.locations");
                    unitValue = d.locationsNum ?? 0;
                }
                let str = `<div>
                            <p style="color:#fff;font-size:14px;">
                                ${t("legend." + (d.valueLabel ?? ""))}
                            </p>
                            <p style="color:#fff;font-size:14px;margin:5px 0;">
                                ${t("layerId." + (d.layerId ?? ""))}
                            </p>
                            <p style="display:flex;justify-content:space-between;align-items:center;font-size:12px;">
                                <span style="color:rgba(255,255,255,0.8);margin-right:10px;">${unitTitle}</span>
                                <span style="color:#fff">${unitValue}</span>
                            </p>`;

                if (!isNumberOfLocations) {
                    str += `<p style="display:flex;justify-content:space-between;font-size:12px;">
                                <span style="color:rgba(255,255,255,0.8);margin-right:10px;">${t("locations." + unit)}</span>
                                <span style="color:#fff">${formatAmount(d.all ?? 0)}</span>
                                </p>
                                <p style="display:flex;justify-content:space-between;font-size:12px;">
                                <span style="color:rgba(255,255,255,0.8);margin-right:10px;">${t("locations.add.currency")}</span>
                                <span style="color:#fff">${resolvedAnalysedLocationSet?.currency ?? ""}</span>
                            </p>`;
                }

                str += `<p style="display:flex;justify-content:space-between;align-items:center;font-size:12px;">
                        <span style="color:rgba(255,255,255,0.8);margin-right:10px;">${t("locations.shareOfKey", { key: distanceAxisConfig?.enabled ? t("drawShape.length") : t("locations." + unit), })}</span>
                        <span style="color:#fff">${shareText}%</span>
                        </p></div>`;

                return str;
            },
        },
        grid: {
            left: 10,
            right: 25,
            bottom: 10,
            top: 10,
            containLabel: true,
        },
        xAxis: {
            type: "value",
            minInterval: 1,
            max: xMax,
            axisLabel: {
                color: "#666",
                formatter: (value: number) => {
                    if (!useDistanceAxis) {
                        return formatKM(value, true);
                    }

                    const safeXMax = Number(xMax) > 0 ? Number(xMax) : 1;
                    const mappedDistance = (Number(value) / safeXMax) * finalDistanceMax;

                    return parseFloat(mappedDistance.toFixed(2));
                },
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: "#ccd6eb",
                }
            },
        },
        yAxis: {
            type: "category",
            axisTick: { show: false },
            axisLabel: { show: false },
            axisLine: {
                show: true,
                lineStyle: {
                    color: "#ccd6eb",
                },
            },
        },
        series,
    };

    return { Ynum, option };
};
